import { useEffect, useState } from 'react';
import {
  CATEGORIES,
  CATEGORY_META,
  PRIORITIES,
  type Category,
  type Priority,
} from '@travel-planner/shared';
import { useCreateItem } from '../lists/useListItems';
import { reverseGeocode } from './useMapData';

export interface PendingPin {
  lng: number;
  lat: number;
}

// Form shown after dropping a pin: a new list item (= map marker) at that
// location, with its title prefilled from reverse-geocoding.
export function NewPinForm({
  tripId,
  countryId,
  pin,
  onClose,
}: {
  tripId: string;
  countryId: string;
  pin: PendingPin;
  onClose: () => void;
}) {
  const create = useCreateItem(tripId, countryId);
  const [title, setTitle] = useState('');
  const [resolving, setResolving] = useState(true);
  const [category, setCategory] = useState<Category>('sight');
  const [priority, setPriority] = useState<Priority>('should');

  useEffect(() => {
    let active = true;
    setResolving(true);
    reverseGeocode(pin.lat, pin.lng)
      .then((r) => {
        if (active && r.label) setTitle(r.label);
      })
      .finally(() => {
        if (active) setResolving(false);
      });
    return () => {
      active = false;
    };
  }, [pin.lat, pin.lng]);

  return (
    <form
      className="glass d-flex flex-column gap-2 p-3 rounded-3"
      style={{ width: 280 }}
      onSubmit={(e) => {
        e.preventDefault();
        if (!title.trim()) return;
        create.mutate(
          { countryId, priority, category, title: title.trim(), lat: pin.lat, lng: pin.lng },
          { onSuccess: onClose },
        );
      }}
    >
      <strong>New marker / item</strong>
      <label className="d-flex flex-column gap-1 small">
        Title
        <input
          className="form-control form-control-sm"
          value={title}
          placeholder={resolving ? 'Resolving location…' : 'Title'}
          onChange={(e) => setTitle(e.target.value)}
        />
      </label>
      <div className="d-flex gap-2">
        <label className="d-flex flex-column gap-1 small flex-grow-1">
          Category
          <select className="form-select form-select-sm" value={category} onChange={(e) => setCategory(e.target.value as Category)}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {CATEGORY_META[c].icon} {CATEGORY_META[c].label}
              </option>
            ))}
          </select>
        </label>
        <label className="d-flex flex-column gap-1 small flex-grow-1">
          Priority
          <select className="form-select form-select-sm" value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="text-body-tertiary" style={{ fontSize: '0.72rem' }}>
        {pin.lat.toFixed(4)}, {pin.lng.toFixed(4)}
      </div>
      <div className="d-flex gap-2">
        <button type="submit" className="btn btn-primary btn-sm" disabled={!title.trim() || create.isPending}>
          {create.isPending ? 'Saving…' : 'Add'}
        </button>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
          Cancel
        </button>
      </div>
    </form>
  );
}
